import type { OrderException, OrderExceptionLine } from '@wms/domain';

// Sorunlu siparişler: an order held because a line matches no product of the warehouse waits for the
// seller's mapping, not for stock. Allocation needs a product first, so "Eksikleri yeniden ayır" cannot
// help it: the queue says so in plain words and offers the mapping screen instead.

/** The order import's reason for such a hold: "Eşleşmeyen ürün: G4-FILTRE-0.5X1" */
const UNMATCHED_REASON = 'Eşleşmeyen ürün';

type Named = Pick<OrderExceptionLine, 'sku' | 'barcode' | 'name'>;

/** The lines nothing can be reserved for until the seller maps them. */
export const unmatchedLines = (e: Pick<OrderException, 'lines'>): readonly OrderExceptionLine[] => e.lines.filter((l) => l.unmatched);

/**
 * Whether a mapping can give the line its product: one the marketplace sent with neither a stock code nor a
 * barcode never shows on the mapping screen (recordUnmatchedLines) and nothing matches it again (releaseHeldOrders).
 */
export const mappable = (l: Named): boolean => Boolean(l.sku || l.barcode);

/**
 * The hold's reason as the queue shows it. Reasons run " · " apart (catalog releaseHeldOrders); the
 * unknown-product one is left out while a line matches nothing (said in plain words instead), and
 * when none does any more but another reason remains (a line the marketplace cancelled needs no product).
 */
export function shownReason(holdReason: string | null, unmatched: boolean): string | null {
  const others = (holdReason ?? '').split(' · ').filter((r) => r.trim() !== '' && !r.startsWith(UNMATCHED_REASON));
  if (others.length > 0) return others.join(' · ');
  return unmatched ? null : holdReason;
}

/** How the marketplace names a line to map: its stock code, else its barcode. */
const channelCode = (l: Named): string => l.sku || l.barcode || '';

/**
 * An order the seller sends from its own stock is not cancelled here: a cancel tells the seller to cancel it on
 * the marketplace too (tellToCancelOnChannel), while the order import drops a held order the marketplace shows
 * sent (channel_shipped_elsewhere).
 */
const SENT_BY_SELLER = 'pazaryerinde kargoya verince sipariş buradan kendiliğinden düşer. Sipariş hiç gönderilmeyecekse iptal edin.';

/**
 * What happened and what to do: "Bu ürün depoda tanımlı değil (pazaryerindeki stok kodu: G4-FILTRE-0.5X1).
 * Satıcı ürünü … sipariş kendiliğinden devam eder. Ürün depoda yoksa satıcı siparişi kendisi gönderir: …" With
 * another reason on the hold the order does not go on once mapped: it waits here for the decision on that one.
 * A line with nothing to map it by keeps the order from going on at all: then only that is said.
 */
export function unmatchedText(lines: readonly Named[], otherReason = false): string {
  const codeless = lines.filter((l) => !mappable(l));
  if (codeless.length > 0) return codelessText(codeless);
  const many = lines.length > 1;
  // a line with no stock code is named by its barcode: then just "kodu"
  const label = `${lines.every((l) => l.sku) ? 'stok ' : ''}${many ? 'kodları' : 'kodu'}`;
  const codes = lines.map(channelCode).join(', ');
  const then = otherReason ? 'bu engel kalkar; sipariş üstteki neden için kararınızı bekler.' : 'sipariş kendiliğinden devam eder.';
  return [
    `${many ? 'Bu ürünler' : 'Bu ürün'} depoda tanımlı değil (pazaryerindeki ${label}: ${codes}).`,
    `Satıcı ${many ? 'ürünleri' : 'ürünü'} Ürünler → Eşleştirme’den depodaki ${many ? 'ürünlere' : 'bir ürüne'} bağlarsa ya da “Ürün olarak ekle” ile tanımlarsa ${then}`,
    `${many ? 'Ürünler' : 'Ürün'} depoda yoksa satıcı siparişi kendisi gönderir: ${SENT_BY_SELLER}`,
  ].join(' ');
}

/** Lines the marketplace sent with neither a stock code nor a barcode, named by the product's name: no mapping helps. */
function codelessText(lines: readonly Named[]): string {
  const many = lines.length > 1;
  return [
    `Pazaryeri ${lines.map((l) => `“${l.name}”`).join(', ')} için stok kodu ya da barkod göndermedi: ${many ? 'ürünler' : 'ürün'} eşleştirilemez, depo bu siparişi gönderemez.`,
    `Satıcı siparişi kendisi gönderebilir: ${SENT_BY_SELLER}`,
    `Sonraki siparişler eşleşsin diye satıcı ${many ? 'ürünlere' : 'ürüne'} pazaryerinde stok kodu ya da barkod eklemeli.`,
  ].join(' ');
}
